import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiKeysApi, HubApiKey } from '@/lib/api';

interface RevokeApiKeyModalProps {
  apiKey: HubApiKey;
  onClose: () => void;
}

/**
 * Revoking is not deleting: the row stays in the list as the record of what was
 * issued, to whom and with what authority. The dialog says so, because "revoke"
 * reads like "remove" and an admin should know the trail survives.
 */
export function RevokeApiKeyModal({ apiKey, onClose }: RevokeApiKeyModalProps) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const revokeMutation = useMutation({
    mutationFn: () => apiKeysApi.revoke(apiKey.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
      onClose();
    },
    onError: (err: any) => setError(err?.message || 'Could not revoke the key'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-secondary/60 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />

      <div className="relative w-full max-w-md bg-white rounded-xl shadow-xl">
        <div className="flex items-start justify-between p-6 pb-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-error/10 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-error" />
            </div>
            <div>
              <h2 className="text-heading-3 text-secondary">Revoke this key?</h2>
              <p className="text-sm text-grey-500 mt-1">
                Anything using it stops working immediately.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 -m-2 text-grey-400 hover:text-secondary rounded transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 pb-6 space-y-4">
          <div className="p-3 rounded-lg bg-grey-50 border border-grey-100">
            <p className="font-medium text-secondary text-sm">{apiKey.name}</p>
            <code className="text-xs text-grey-500 font-mono">{apiKey.prefix}…</code>
          </div>

          <p className="text-sm text-grey-600">
            The key stays in the list as a record of what had access — it just stops authenticating.
            It cannot be un-revoked; issue a new key if you need one again.
          </p>

          {error && <div className="p-3 rounded-lg bg-error/10 text-error text-sm">{error}</div>}

          <div className="flex items-center justify-end gap-3">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => revokeMutation.mutate()}
              isLoading={revokeMutation.isPending}
            >
              Revoke key
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
